import { inject, Injectable } from '@angular/core';
import { AccountFilter } from '../filters/AccountFilter';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import {
	AIChatConversatieStatus,
	CronWorkflow,
	CumLaudeLicentieInfo,
	CumLaudeVersionInfo,
	DefaultParallelism,
	ExecuteMigrationRequest,
	MigrationStatistics,
	RAfwezigheidsreden,
	RAIChatBehandeling,
	RAIChatConversatie,
	RAIChatOpmerkingInhoud,
	RBasisvaardighedenNormTable,
	RBestuur,
	RBestuurPrimer,
	RBezochtePagina,
	RCumLaudeAccount,
	RCumLaudeAccountAdditionalObjectKey,
	RDataExportFavoriet,
	RDatasnapResult,
	RDatasnapUrl,
	RestErrorAdditionalParameters,
	RestErrorMessage,
	RestErrorMessageEnum,
	RFavoriet,
	RFavorietMap,
	RFeedback,
	RGedeeldePaginaPersoon,
	RGedeeldePaginaUrl,
	RInstelling,
	RInstellingAdditionalObjectKey,
	RInstellingPrimer,
	RJobAudit,
	RJobrun,
	RJobrunInstelling,
	RLeerlingSelectie,
	RMagisterInstelling,
	RNavigatieFeedbackSnooze,
	RRecentBezochtePagina,
	RRol,
	RVakUitsluiting,
	RVestiging,
	RVestigingClusterVSO,
	RVoorkeuren,
	SubmitWorkflow,
	Workflow,
	WorkflowTemplate,
} from '@cumlaude/service-contract';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpStatusCode } from '@angular/common/http';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { BugsnagService } from '@cumlaude/bugsnag';
import { VestigingFilter } from '../filters/VestigingFilter';
import { isNil, omit } from 'lodash-es';
import { AuthService } from '@cumlaude/shared-authentication';
import { VestigingClusterVSOFilter } from '../filters/VestigingClusterVSOFilter';
import { AIChatConversatieFilter } from '../filters/AIChatConversatieFilter';
import { JobAuditFilter } from '../filters/JobAuditFilter';
import { JobrunFilter } from '../filters/JobrunFilter';
import { DataExportFavorietFilter } from '../filters/DataExportFavorietFilter';
import { JobrunInstellingFilter } from '../filters/JobrunInstellingFilter';
import { GedeeldePaginaPersoonFilter } from '../filters/GedeeldePaginaPersoonFilter';

type RequestParams =
	| HttpParams
	| {
			[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
	  };

type MethodParams = {
	[param: string]: string | number | boolean | undefined | null | ReadonlyArray<string | number | boolean | undefined | null>;
};

export type Pagina<T> = {
	items: T[];
	totaal: number;
};

@Injectable({
	providedIn: 'root',
})
export class RestService {
	protected readonly http = inject(HttpClient);
	protected readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	protected readonly bugsnag = inject(BugsnagService);
	protected readonly authService = inject(AuthService);

	getMyAccount(): Observable<RCumLaudeAccount> {
		const url = `${this.envConfig.restUrl}/account/me`;
		const params = { additional: 'autorisaties' };
		return this.getSingle(url, params);
	}

	getAccountsByInstelling(instelling: RInstelling): Observable<RCumLaudeAccount[]> {
		const params = {
			sort: ['desc-support', 'asc-naam'],
			instelling: `${instelling.id}`,
			actief: 'true',
		};
		return this.getAccounts(params);
	}

	getAccountsFromFilter(filter: AccountFilter, additional?: RCumLaudeAccountAdditionalObjectKey[]): Observable<RCumLaudeAccount[]> {
		const { actief, ...rest } = filter;
		const params = this.getParams({
			sort: 'asc-naam',
			...rest,
			additional,
			support: 'false',
			actief: `${actief ?? true}`,
		});
		return this.getAccounts(params);
	}

	private getAccounts(params: RequestParams): Observable<RCumLaudeAccount[]> {
		const url = `${this.envConfig.restUrl}/account`;
		return this.get(url, params);
	}

	getAccountMedewerkerExternIds(): Observable<string[]> {
		const url = `${this.envConfig.restUrl}/account/medewerkerExternIds`;
		return this.getStringList(url);
	}

	postAccount(account: RCumLaudeAccount, linkCredentials: boolean): Observable<RCumLaudeAccount> {
		const url = `${this.envConfig.restUrl}/account`;
		const params = linkCredentials ? { link_credentials: true } : undefined;
		return this.postSingle(url, account, params);
	}

	putAccount(account: RCumLaudeAccount, linkCredentials: boolean): Observable<RCumLaudeAccount> {
		const url = `${this.envConfig.restUrl}/account/${account.id}`;
		const params = linkCredentials ? { link_credentials: true } : undefined;
		return this.put(url, account, params);
	}

	deleteAccount(account: RCumLaudeAccount): Observable<void> {
		const url = `${this.envConfig.restUrl}/account/${account.id}`;
		return this.delete(url);
	}

	resetAccountPassword(account: RCumLaudeAccount): Observable<void> {
		const url = `${this.envConfig.restUrl}/account/${account.id}/resetPassword`;
		return this.postVoid(url);
	}

	resetAccountTotp(account: RCumLaudeAccount): Observable<void> {
		const url = `${this.envConfig.restUrl}/account/${account.id}/remove2FA`;
		return this.postVoid(url);
	}

	getBasisvaardighedenNormen(schooljaar: string[], vaardigheid: string, vestigingId: string[]): Observable<RBasisvaardighedenNormTable[]> {
		const url = `${this.envConfig.restUrl}/basisvaardigheden_norm/table`;
		const params = {
			schooljaar,
			vaardigheid,
			vestigingId,
		};
		return this.get(url, params);
	}

	postBasisvaardighedenNormen(tables: RBasisvaardighedenNormTable[]): Observable<RBasisvaardighedenNormTable[]> {
		const url = `${this.envConfig.restUrl}/basisvaardigheden_norm/table`;
		return this.post(url, tables);
	}

	getBestuur(id: number): Observable<RBestuur> {
		const url = `${this.envConfig.restUrl}/bestuur/${id}`;
		return this.getSingle(url);
	}

	getBesturen(): Observable<RBestuur[]> {
		const url = `${this.envConfig.restUrl}/bestuur`;
		const params = {
			sort: ['asc-naam'],
		};
		return this.get(url, params);
	}

	postBestuur(bestuur: RBestuur): Observable<RBestuur> {
		const url = `${this.envConfig.restUrl}/bestuur`;
		return this.postSingle(url, bestuur);
	}

	putBestuur(bestuur: Partial<RBestuur | RBestuurPrimer>): Observable<RBestuur> {
		const url = `${this.envConfig.restUrl}/bestuur/${bestuur.id}`;
		return this.put(url, <Partial<RBestuur>>{
			...omit(bestuur, 'additionalObjects', 'instellingen'),
			$type: 'bestuur.RBestuur',
		});
	}

	deleteBestuur(bestuur: RBestuur): Observable<void> {
		const url = `${this.envConfig.restUrl}/bestuur/${bestuur.id}`;
		return this.http.delete<void>(url).pipe(
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	getDataserviceVersionInfo(): Observable<CumLaudeVersionInfo> {
		const url = `${this.envConfig.dataUrl!}/version`;
		return this.getSingle(url);
	}

	getDatasnapResults(urlId: string): Observable<RDatasnapResult[]> {
		const url = `${this.envConfig.restUrl}/datasnap_url/${urlId}/result`;
		const params = {
			sort: 'desc-tsRequest',
		};
		return this.get(url, params);
	}

	getDatasnapUrls(): Observable<RDatasnapUrl[]> {
		const url = `${this.envConfig.restUrl}/datasnap_url`;
		const params = {
			actief: true,
		};
		return this.get(url, params);
	}

	postDatasnapUrls(datasnapUrls: RDatasnapUrl[]): Observable<RDatasnapUrl[]> {
		const url = `${this.envConfig.restUrl}/datasnap_url`;
		return this.post(url, datasnapUrls);
	}

	getFavorieten(): Observable<RFavoriet[]> {
		const url = `${this.envConfig.restUrl}/favoriet`;
		const params = {
			sort: 'asc-naam',
		};
		return this.getAlles(url, params);
	}

	postFavoriet(favoriet: Partial<RFavoriet>): Observable<RFavoriet> {
		const url = `${this.envConfig.restUrl}/favoriet`;
		return this.postSingle(url, favoriet);
	}

	putFavoriet(favoriet: Partial<RFavoriet>): Observable<RFavoriet> {
		const url = `${this.envConfig.restUrl}/favoriet/${favoriet.id}`;
		return this.put(url, favoriet);
	}

	deleteFavoriet(favoriet: RFavoriet): Observable<void> {
		const url = `${this.envConfig.restUrl}/favoriet/${favoriet.id}`;
		return this.delete(url);
	}

	getFavorietMappen(): Observable<RFavorietMap[]> {
		const url = `${this.envConfig.restUrl}/favoriet_map`;
		const params = {
			sort: 'asc-naam',
		};
		return this.getAlles(url, params);
	}

	postFavorietMap(favorietMap: Partial<RFavorietMap>): Observable<RFavorietMap> {
		const url = `${this.envConfig.restUrl}/favoriet_map`;
		return this.postSingle(url, favorietMap);
	}

	postFavorietMappen(favorietMappen: RFavorietMap[]): Observable<RFavorietMap[]> {
		const url = `${this.envConfig.restUrl}/favoriet_map`;
		return this.post(url, favorietMappen);
	}

	putFavorietMap(favorietMap: Partial<RFavorietMap>): Observable<RFavorietMap> {
		const url = `${this.envConfig.restUrl}/favoriet_map/${favorietMap.id}`;
		return this.put(url, favorietMap);
	}

	deleteFavorietMap(favorietMap: RFavorietMap): Observable<void> {
		const url = `${this.envConfig.restUrl}/favoriet_map/${favorietMap.id}`;
		return this.delete(url);
	}

	postFeedback(feedback: RFeedback): Observable<void> {
		const url = `${this.envConfig.restUrl}/feedback`;
		return this.http.post<void>(url, feedback);
	}

	getRecentePaginas(rol: RRol): Observable<RRecentBezochtePagina[]> {
		const url = `${this.envConfig.restUrl}/recente_pagina`;
		const params = { rol, padBevatNiet: '/details', sort: 'desc-laatstBezocht' };
		const headers = new HttpHeaders().set('Range', 'items=0-9');
		return this.get(url, params, headers);
	}

	postRecentePagina(bezoek: RBezochtePagina): Observable<void> {
		const url = `${this.envConfig.restUrl}/recente_pagina/bijwerken`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.post<void>(url, bezoek)),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	putVoorkeuren(voorkeuren: RVoorkeuren): Observable<void> {
		const url = `${this.envConfig.restUrl}/account/me/voorkeuren`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.put<void>(url, voorkeuren)),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	putNavigatieFeedbackSnooze(snooze: RNavigatieFeedbackSnooze): Observable<void> {
		const url = `${this.envConfig.restUrl}/account/me/navigatie-feedback-snooze`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.put<void>(url, snooze)),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	getProductboardToken(): Observable<string> {
		const url = `${this.envConfig.restUrl}/feedback`;
		return this.http.get(url, { responseType: 'text' });
	}

	getGedeeldePaginaPersonen(filter: GedeeldePaginaPersoonFilter, vanaf: number): Observable<Pagina<RGedeeldePaginaPersoon>> {
		const url = `${this.envConfig.restUrl}/gedeelde_pagina_persoon`;
		const params = this.getParams({
			sort: 'desc-verzonden',
			ontvanger: filter.ontvanger?.id,
			verzender: filter.verzender?.id,
		});
		return this.getPage(url, params, vanaf, 100);
	}

	getAantalOngelezenBerichten(ontvanger: RCumLaudeAccount): Observable<number> {
		const url = `${this.envConfig.restUrl}/gedeelde_pagina_persoon`;
		const params = this.getParams({ ontvanger: ontvanger.id, gelezen: false });
		return this.getPage<RGedeeldePaginaPersoon>(url, params, 0, 1).pipe(map((pagina) => pagina.totaal));
	}

	postGedeeldePaginaPersoon(gedeeldePaginaPersoon: Partial<RGedeeldePaginaPersoon>): Observable<RGedeeldePaginaPersoon> {
		const url = `${this.envConfig.restUrl}/gedeelde_pagina_persoon`;
		return this.postSingle(url, gedeeldePaginaPersoon);
	}

	putGedeeldePaginaPersoon(gedeeldePaginaPersoon: Partial<RGedeeldePaginaPersoon>): Observable<RGedeeldePaginaPersoon> {
		const url = `${this.envConfig.restUrl}/gedeelde_pagina_persoon/${gedeeldePaginaPersoon.id}`;
		return this.put(url, gedeeldePaginaPersoon);
	}

	deleteGedeeldePaginaPersoon(gedeeldePaginaPersoon: RGedeeldePaginaPersoon): Observable<void> {
		const url = `${this.envConfig.restUrl}/gedeelde_pagina_persoon/${gedeeldePaginaPersoon.id}`;
		return this.http.delete<void>(url).pipe(
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	getGedeeldePaginaUrl(uuid: string): Observable<RGedeeldePaginaUrl | undefined> {
		const url = `${this.envConfig.restUrl}/gedeelde_pagina_url`;
		const params = {
			uuid,
		};
		return this.get<RGedeeldePaginaUrl>(url, params).pipe(map((items) => (items.length > 0 ? items[0] : undefined)));
	}

	postGedeeldePaginaUrl(gedeeldePaginaUrl: RGedeeldePaginaUrl): Observable<RGedeeldePaginaUrl> {
		const url = `${this.envConfig.restUrl}/gedeelde_pagina_url`;
		return this.postSingle(url, gedeeldePaginaUrl);
	}

	getInstelling(idOrPrimer: number | string | RInstellingPrimer, additional?: RInstellingAdditionalObjectKey[]): Observable<RInstelling> {
		const id = typeof idOrPrimer === 'object' ? idOrPrimer.id : idOrPrimer;
		const url = `${this.envConfig.restUrl}/instelling/${id}`;
		const params = this.getParams({ additional });
		return this.getSingle(url, params);
	}

	getInstellingen(bestuur?: RBestuur, additional?: RInstellingAdditionalObjectKey[]): Observable<RInstelling[]> {
		const url = `${this.envConfig.restUrl}/instelling`;
		const params = this.getParams({
			verwijderd: false,
			bestuur: bestuur?.id,
			additional,
			sort: ['desc-actief', 'asc-naam'],
		});
		return this.get(url, params);
	}

	putInstelling(instelling: Partial<RInstelling | RInstellingPrimer>): Observable<RInstelling> {
		const url = `${this.envConfig.restUrl}/instelling/${instelling.id}`;
		return this.put(url, <Partial<RInstelling>>{
			...omit(instelling, 'additionalObjects'),
			$type: 'instelling.RInstelling',
		});
	}

	getLeerlingSelectie(id: string): Observable<RLeerlingSelectie> {
		const url = `${this.envConfig.restUrl}/leerlingselectie/${id}`;
		return this.getSingle(url);
	}

	getLeerlingSelecties(): Observable<RLeerlingSelectie[]> {
		const url = `${this.envConfig.restUrl}/leerlingselectie`;
		const params = {
			sort: 'asc-naam',
		};
		return this.get(url, params);
	}

	postLeerlingSelectie(leerlingSelectie: Partial<RLeerlingSelectie>): Observable<RLeerlingSelectie> {
		const url = `${this.envConfig.restUrl}/leerlingselectie`;
		return this.postSingle(url, leerlingSelectie);
	}

	putLeerlingSelectie(leerlingSelectie: Partial<RLeerlingSelectie>): Observable<RLeerlingSelectie> {
		const url = `${this.envConfig.restUrl}/leerlingselectie/${leerlingSelectie.id}`;
		return this.put(url, leerlingSelectie);
	}

	deleteLeerlingSelectie(id: string): Observable<void> {
		const url = `${this.envConfig.restUrl}/leerlingselectie/${id}`;
		return this.delete(url);
	}

	getLicentieInfo(): Observable<CumLaudeLicentieInfo> {
		const url = `${this.envConfig.restUrl}/licentie`;
		return this.getSingle(url);
	}

	getMagisterInstelling(idOrObject: number | string | RInstellingPrimer | RInstelling): Observable<RMagisterInstelling> {
		const id = typeof idOrObject === 'object' ? idOrObject.id : idOrObject;
		const url = `${this.envConfig.restUrl}/magister_instelling/${id}`;
		return this.getSingle(url);
	}

	postMagisterInstelling(instelling: Partial<RMagisterInstelling>): Observable<RMagisterInstelling> {
		const url = `${this.envConfig.restUrl}/magister_instelling`;
		return this.postSingle(url, instelling);
	}

	putMagisterInstelling(instelling: Partial<RMagisterInstelling>): Observable<RMagisterInstelling> {
		const url = `${this.envConfig.restUrl}/magister_instelling/${instelling.id}`;
		return this.put(url, instelling);
	}

	getReleaseNotes(): Observable<string[]> {
		const url = `${this.envConfig.restUrl}/release-notes`;
		return this.getStringList(url);
	}

	getRestVersionInfo(): Observable<CumLaudeVersionInfo> {
		const url = `${this.envConfig.restUrl}/version`;
		return this.getSingle(url);
	}

	getVakUitsluitingen(): Observable<RVakUitsluiting[]> {
		const url = `${this.envConfig.restUrl}/vak_uitsluiting`;
		const params = {
			sort: 'asc-vakNaam',
		};
		return this.get(url, params);
	}

	postVakUitsluiting(vakUitsluiting: RVakUitsluiting): Observable<RVakUitsluiting> {
		const url = `${this.envConfig.restUrl}/vak_uitsluiting`;
		return this.postSingle(url, vakUitsluiting);
	}

	putVakUitsluiting(vakUitsluiting: RVakUitsluiting): Observable<RVakUitsluiting> {
		const url = `${this.envConfig.restUrl}/vak_uitsluiting/${vakUitsluiting.id}`;
		return this.put(url, vakUitsluiting);
	}

	getAfwezigheidsredenen(): Observable<RAfwezigheidsreden[]> {
		const url = `${this.envConfig.restUrl}/afwezigheidsreden`;
		const params = { sort: ['asc-reden'] };
		return this.get(url, params);
	}

	putAfwezigheidsreden(afwezigheidsreden: RAfwezigheidsreden): Observable<RAfwezigheidsreden> {
		const url = `${this.envConfig.restUrl}/afwezigheidsreden/${afwezigheidsreden.id}`;
		return this.put(url, afwezigheidsreden);
	}

	getVestigingClusterVSOs(filter: VestigingClusterVSOFilter): Observable<RVestigingClusterVSO[]> {
		const url = `${this.envConfig.restUrl}/vestiging_cluster_vso`;
		const params = this.getParams({ sort: ['asc-schooljaar'], ...filter });
		return this.get(url, params);
	}

	putVestigingClusterVSO(vestigingClusterVSO: RVestigingClusterVSO): Observable<RVestigingClusterVSO> {
		const url = `${this.envConfig.restUrl}/vestiging_cluster_vso/${vestigingClusterVSO.id}`;
		return this.put(url, vestigingClusterVSO);
	}

	getVestigingen({ cumlaudeActief, instelling, vanBestuur, vso }: VestigingFilter): Observable<RVestiging[]> {
		const url = `${this.envConfig.restUrl}/vestiging${vanBestuur ? '/bestuurVestigingen' : ''}`;
		const params = this.getParams({
			sort: ['desc-actief', 'asc-naam'],
			cumlaudeActief: cumlaudeActief ? 'TRUE' : undefined,
			instelling: Array.isArray(instelling) ? instelling.map((i) => i.id!) : instelling?.id,
			vso: Number(vso) || undefined,
		});
		return this.get(url, params);
	}

	getBeheerVestigingen(): Observable<RVestiging[]> {
		const url = `${this.envConfig.restUrl}/vestiging/beheerderVestigingen`;
		const params = {
			sort: ['desc-actief', 'asc-naam'],
			cumlaudeActief: 'TRUE',
		};
		return this.get(url, params);
	}

	putVestiging(vestiging: Partial<RVestiging>): Observable<RVestiging> {
		const url = `${this.envConfig.restUrl}/vestiging/${vestiging.id}`;
		return this.put(url, vestiging);
	}

	isArgoWorkflowsAvailable(): Observable<boolean> {
		const url = `${this.envConfig.restUrl}/argoworkflows/available`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.get<boolean>(url, {
					observe: 'body',
					responseType: 'json',
				})
			),
			catchError((resp) => {
				if (resp.status === HttpStatusCode.ServiceUnavailable) return of(false);
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	getDefaultParallelism(): Observable<DefaultParallelism> {
		const url = `${this.envConfig.restUrl}/argoworkflows/defaultparallelism`;
		return this.getSingle(url);
	}

	getWorkflows(): Observable<Workflow[]> {
		const url = `${this.envConfig.restUrl}/argoworkflows/workflows/list`;
		return this.get(url, {});
	}

	getCronWorkflows(): Observable<CronWorkflow[]> {
		const url = `${this.envConfig.restUrl}/argoworkflows/cronworkflows/list`;
		return this.get(url, {});
	}

	getWorkflowTemplates(): Observable<WorkflowTemplate[]> {
		const url = `${this.envConfig.restUrl}/argoworkflows/workflowtemplates/list`;
		return this.get(url, {});
	}

	suspendCronWorkflow(name: string) {
		const url = `${this.envConfig.restUrl}/argoworkflows/cronworkflows/${name}/suspend`;
		return this.postVoid(url);
	}

	resumeCronWorkflow(name: string) {
		const url = `${this.envConfig.restUrl}/argoworkflows/cronworkflows/${name}/resume`;
		return this.postVoid(url);
	}

	submitWorkflow(submitWorkflow: SubmitWorkflow): Observable<Workflow> {
		const url = `${this.envConfig.restUrl}/argoworkflows/workflows/submit`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.post<Workflow>(url, submitWorkflow, {
					observe: 'body',
					responseType: 'json',
				})
			),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	getJobruns(filter: JobrunFilter = {}): Observable<RJobrun[]> {
		const url = `${this.envConfig.restUrl}/jobrun`;
		const params = {
			sort: 'asc-id',
			...filter,
		};
		return this.get(url, this.getParams(params));
	}

	getJobrunInstellingen({ instelling, bulkload, workflowUid, limit }: JobrunInstellingFilter): Observable<RJobrunInstelling[]> {
		const url = `${this.envConfig.restUrl}/jobruninstelling`;
		const params = {
			sort: ['asc-instelling.naam', 'desc-nrJobrun'],
			bulkload: bulkload === undefined ? undefined : Number(bulkload),
			instelling: instelling?.id,
			workflowUid,
		};
		const headers = limit ? new HttpHeaders().set('Range', `items=0-${limit - 1}`) : undefined;
		return this.get(url, this.getParams(params), headers);
	}

	getJobAudits(filter: JobAuditFilter = {}): Observable<RJobAudit[]> {
		const url = `${this.envConfig.restUrl}/job_audit`;
		const params = {
			...filter,
		};
		return this.get(url, this.getParams(params));
	}

	postJobAudit(jobAudit: RJobAudit): Observable<RJobAudit> {
		const url = `${this.envConfig.restUrl}/job_audit`;
		return this.postSingle(url, jobAudit);
	}

	getAIChatConversatie(id: number): Observable<RAIChatConversatie> {
		const url = `${this.envConfig.restUrl}/aichatconversatie/${id}`;
		return this.getSingle(url);
	}

	getAIChatConversaties(filter: AIChatConversatieFilter = {}): Observable<RAIChatConversatie[]> {
		const url = `${this.envConfig.restUrl}/aichatconversatie`;
		const params = {
			sort: 'desc-lastModified',
			...filter,
		};
		return this.get(url, this.getParams(params));
	}

	postAIChatConversatie(aiChatConversatie: Partial<RAIChatConversatie>): Observable<RAIChatConversatie> {
		const url = `${this.envConfig.restUrl}/aichatconversatie`;
		return this.postSingle(url, aiChatConversatie);
	}

	putAIChatConversatie(aiChatConversatie: Partial<RAIChatConversatie>): Observable<RAIChatConversatie> {
		const url = `${this.envConfig.restUrl}/aichatconversatie/${aiChatConversatie.id}`;
		return this.put(url, aiChatConversatie);
	}

	deleteAIChatConversatie(aiChatConversatie: RAIChatConversatie): Observable<void> {
		const url = `${this.envConfig.restUrl}/aichatconversatie/${aiChatConversatie.id}`;
		return this.delete(url);
	}

	getAIChatBehandeling(conversatieId: string): Observable<RAIChatBehandeling> {
		const url = `${this.envConfig.restUrl}/aichatbehandeling/${conversatieId}`;
		return this.getSingle(url);
	}

	/**
	 * Alleen bereikbaar voor admins. De status staat in het pad, de behandelaar en het
	 * wijzigingstijdstip worden server-side gezet en komen in het antwoord terug.
	 */
	putAIChatBehandelingStatus(conversatieId: string, status: AIChatConversatieStatus): Observable<RAIChatBehandeling> {
		const url = `${this.envConfig.restUrl}/aichatbehandeling/${conversatieId}/status/${status}`;
		return this.requestRaw<RAIChatBehandeling>('PUT', url);
	}

	postAIChatOpmerking(conversatieId: string, inhoud: RAIChatOpmerkingInhoud): Observable<RAIChatBehandeling> {
		const url = `${this.envConfig.restUrl}/aichatbehandeling/${conversatieId}/opmerking`;
		return this.requestRaw<RAIChatBehandeling>('POST', url, inhoud);
	}

	putAIChatOpmerking(conversatieId: string, opmerkingId: string, inhoud: RAIChatOpmerkingInhoud): Observable<RAIChatBehandeling> {
		const url = `${this.envConfig.restUrl}/aichatbehandeling/${conversatieId}/opmerking/${opmerkingId}`;
		return this.requestRaw<RAIChatBehandeling>('PUT', url, inhoud);
	}

	deleteAIChatOpmerking(conversatieId: string, opmerkingId: string): Observable<RAIChatBehandeling> {
		const url = `${this.envConfig.restUrl}/aichatbehandeling/${conversatieId}/opmerking/${opmerkingId}`;
		return this.requestRaw<RAIChatBehandeling>('DELETE', url);
	}

	getDataExportFavorieten(filter: DataExportFavorietFilter = {}): Observable<RDataExportFavoriet[]> {
		const url = `${this.envConfig.restUrl}/dataexportfavoriet`;
		const params = {
			sort: 'asc-naam',
			...filter,
		};
		return this.get(url, this.getParams(params));
	}

	postDataExportFavoriet(dataExportFavoriet: Partial<RDataExportFavoriet>): Observable<RDataExportFavoriet> {
		const url = `${this.envConfig.restUrl}/dataexportfavoriet`;
		return this.postSingle(url, dataExportFavoriet);
	}

	putDataExportFavoriet(dataExportFavoriet: Partial<RDataExportFavoriet>): Observable<RDataExportFavoriet> {
		const url = `${this.envConfig.restUrl}/dataexportfavoriet/${dataExportFavoriet.id}`;
		return this.put(url, dataExportFavoriet);
	}

	deleteDataExportFavoriet(dataExportFavoriet: RDataExportFavoriet): Observable<void> {
		const url = `${this.envConfig.restUrl}/dataexportfavoriet/${dataExportFavoriet.id}`;
		return this.delete(url);
	}

	executeMigration(request: ExecuteMigrationRequest): Observable<MigrationStatistics> {
		const url = `${this.envConfig.restUrl}/migratie/execute`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.post<MigrationStatistics>(url, request)),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	private getParams(params: MethodParams): RequestParams {
		return Object.fromEntries(
			Object.entries(params).filter(([_, value]) => !isNil(value) && !(Array.isArray(value) && value.length === 0) && value !== '')
		) as RequestParams;
	}

	private getSingle<T>(url: string, params?: RequestParams): Observable<T> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.get<T>(url, {
					params,
					observe: 'body',
					responseType: 'json',
				})
			),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	private getStringList(url: string, params?: RequestParams): Observable<string[]> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.get<string[]>(url, {
					params,
					observe: 'body',
					responseType: 'json',
				})
			),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	private get<T>(url: string, params?: RequestParams, headers?: HttpHeaders): Observable<T[]> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.get<{ items: T[] }>(url, {
					params,
					headers,
					observe: 'body',
					responseType: 'json',
				})
			),
			map((resp) => resp.items),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	/**
	 * Voorkomt dat een lijst die groter is dan één pagina stilzwijgend wordt afgekapt.
	 */
	private getAlles<T>(url: string, params: RequestParams | undefined, chunk = 100): Observable<T[]> {
		return this.getPage<T>(url, params, 0, chunk).pipe(
			switchMap((eerstePagina) => {
				const resterendeVanaf: number[] = [];
				for (let vanaf = chunk; vanaf < eerstePagina.totaal; vanaf += chunk) resterendeVanaf.push(vanaf);

				if (resterendeVanaf.length === 0) return of(eerstePagina.items);

				return forkJoin(resterendeVanaf.map((vanaf) => this.getPage<T>(url, params, vanaf, chunk))).pipe(
					map((paginas) => [...eerstePagina.items, ...paginas.flatMap((pagina) => pagina.items)])
				);
			})
		);
	}

	/**
	 * Haalt één pagina van `aantal` items op vanaf offset `vanaf`, met een gegarandeerd exact `totaal`
	 * (niet Cobra's schatting bij een volle pagina).
	 */
	private getPage<T>(url: string, params: RequestParams | undefined, vanaf: number, aantal: number): Observable<Pagina<T>> {
		const headers = new HttpHeaders().set('Range', `items=${vanaf}-${vanaf + aantal - 1}`).set('topicus-Range-Count', 'Exact');
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.get<{ items: T[] }>(url, {
					params,
					headers,
					observe: 'response',
					responseType: 'json',
				})
			),
			map((resp) => {
				const items = resp.body?.items ?? [];
				return { items, totaal: this.parseTotaalUitContentRange(resp.headers.get('Content-Range')) ?? items.length };
			}),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	private parseTotaalUitContentRange(contentRange: string | null): number | undefined {
		const match = contentRange ? /\/(\d+)$/.exec(contentRange) : null;
		return match ? Number(match[1]) : undefined;
	}

	private put<T>(url: string, item: Partial<T>, params?: RequestParams): Observable<T> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.put<T>(url, item, { params })),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	private postSingle<T>(url: string, item: Partial<T>, params?: RequestParams): Observable<T> {
		return this.post<T>(url, [item], params).pipe(map((items) => items[0]));
	}

	private post<T>(url: string, items: Partial<T>[], params?: RequestParams): Observable<T[]> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.post<{ items: T[] }>(url, { items: items }, { params })),
			map((accs) => accs.items),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	private postVoid(url: string): Observable<void> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.post<void>(url, {})),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	private delete(url: string): Observable<void> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.delete<void>(url)),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	/**
	 * Voor losse endpoints buiten de cobra-CRUD-resources, waarvan het antwoord niet in de
	 * `{ items: [...] }`-omhulling van `post`/`put` past en niet noodzakelijk hetzelfde type heeft als
	 * de body.
	 */
	private requestRaw<T>(method: 'POST' | 'PUT' | 'DELETE', url: string, body?: unknown): Observable<T> {
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() => this.http.request<T>(method, url, { body })),
			catchError((resp) => {
				throw this.parseRestErrorResponse(resp);
			})
		);
	}

	/**
	 * Een afgebroken request (status 0) krijgt ABORTED en wordt niet gemeld; enableBugsnag filtert die ook uit de ErrorHandler.
	 */
	protected parseRestErrorResponse(resp: HttpErrorResponse | RestErrorMessage): RestErrorMessage {
		if (!(resp instanceof HttpErrorResponse)) {
			if (resp.error !== undefined)
				return resp;

			this.bugsnag.notify(resp);

			return this.buildRestErrorMessage('Unknown error occurred', {
				error: RestErrorMessageEnum.UNKNOWN,
			});
		}

		if (resp.status === 0) {
			return this.buildRestErrorMessage('Request aborted', {
				error: RestErrorMessageEnum.ABORTED,
			});
		}
		if (resp.error?.applicationError) {
			return this.buildRestErrorMessage('Rest backend error', {
				error: resp.error.applicationError,
				parameters: resp.error.applicationErrorParameters,
			});
		}
		if (resp.status === HttpStatusCode.Unauthorized) {
			return this.buildRestErrorMessage('Unauthorized', {
				error: RestErrorMessageEnum.NO_ACCESS,
			});
		}

		this.bugsnag.notify(resp);

		return this.buildRestErrorMessage('Unknown error occurred', {
			error: RestErrorMessageEnum.UNKNOWN,
		});
	}

	private buildRestErrorMessage(message: string, additionalParameters: RestErrorAdditionalParameters = {}): RestErrorMessage {
		const error = new Error(message) as RestErrorMessage;
		Object.assign(error, additionalParameters);
		return error;
	}
}
